"use client";

import { Button, IconButton, Tooltip } from "@zapier/design-system-beta";
import classNames from "classnames";
import styles from "./TemplateBookmarkIconButton.module.css";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import { useTemplateBookmark } from "./TemplateBookmarkContext";
import { Icon } from "@zapier/design-system";

type BookmarkButtonVariant = "icon" | "button";

export const TemplateBookmarkIconButton = ({
  templateId,
  templateSlug,
  isZapTemplate,
  buttonVariant = "icon",
  className,
}: {
  templateId: string;
  templateSlug: string;
  isZapTemplate: boolean;
  buttonVariant?: BookmarkButtonVariant;
  className?: string;
}) => {
  const {
    bookmarkedTemplates,
    createUserTemplateBookmark,
    deleteUserTemplateBookmark,
    templateBookmarkChanging,
  } = useTemplateBookmark();

  const isTemplateBookmarked = bookmarkedTemplates?.includes(templateId);
  const label = isTemplateBookmarked ? "Unsave template" : "Save template";

  const handleTemplateBookmarkClick = () => {
    isTemplateBookmarked
      ? deleteUserTemplateBookmark(templateId)
      : createUserTemplateBookmark(templateId);

    emitUserInteractionEvent(
      UserInteractionEventName.templateBookmarkButtonClicked,
      {
        event_action: "click",
        interaction_goal: label,
        option_selected: templateId,
        custom_tracking_fields: {
          template_slug: templateSlug,
          is_zap_template: `${isZapTemplate}`,
        },
      },
    );
  };

  if (buttonVariant === "button") {
    return (
      <Button
        iconStart={
          <Icon
            name={
              isTemplateBookmarked ? "actionBookmarkFill" : "actionBookmark"
            }
            size={16}
          />
        }
        onPress={handleTemplateBookmarkClick}
        variant="tertiary"
        data-selected={isTemplateBookmarked}
        className={classNames(styles.bookmarkFormButton, className)}
        isDisabled={templateBookmarkChanging === templateId}
      >
        {isTemplateBookmarked ? "Saved" : "Save"}
      </Button>
    );
  }

  return (
    <Tooltip content={label}>
      <IconButton
        isDisabled={templateBookmarkChanging === templateId}
        aria-label={label}
        size="small"
        className={classNames([
          styles.button,
          isTemplateBookmarked ? styles.saved : styles.default,
          className,
        ])}
        name={isTemplateBookmarked ? "actionBookmarkFill" : "actionBookmark"}
        variant="tertiary"
        onPress={handleTemplateBookmarkClick}
      />
    </Tooltip>
  );
};
